import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Stack, Text } from '@ltvco/refresh-lib/theme';
import { AIPill } from '@ltvco/refresh-lib/v2';
import { Download, Psychology } from '@mui/icons-material';
import { useContext, useState } from 'react';

import {
  InlineLoading,
  OpinionContentContainer,
  OpinionContentHeader,
  OpinionContentTitle,
  OpinionContentHtml,
  StyledActionButton,
  OpinionSummaryContainer,
  OpinionSummaryTitle,
  OpinionSummaryContent,
  DisclaimerContainer,
  OpinionDisclaimerText,
  OpinionSummaryLoadingOverlay,
  opinionSummaryLoadingCardStyles,
  opinionSummaryLoadingIconStyles,
  opinionSummaryLoadingTitleStyles,
  opinionSummaryLoadingTextStyles,
} from '../../../shared';
import { useCourtCaseOpinionsSummary } from '../../../shared/hooks';
import { type DisplayOpinion } from '../../../types';
import { cleanOpinionType, processHtmlWithCitations } from '../../../utils';

interface OpinionContentProps {
  opinion: DisplayOpinion;
}

export const OpinionContent = ({ opinion }: OpinionContentProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const [shouldFetchSummary, setShouldFetchSummary] = useState(false);

  const isHeadmatter = opinion.type?.toLowerCase() === 'headmatter';
  const opinionWithContent = opinion as {
    plain_text?: string;
    html_with_citations?: string;
  };
  const shouldShowSummaryButton =
    !isHeadmatter &&
    !!(opinionWithContent.plain_text || opinionWithContent.html_with_citations);

  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    isError: hasSummaryError,
  } = useCourtCaseOpinionsSummary(opinion, shouldFetchSummary);

  const handleDownloadClick = () => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'download_pdf',
      section_name: 'court_cases',
      trigger: 'pdf_button_clicked',
    });
  };

  const handleSummarizeClick = () => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'opinion_overview',
      section_name: 'court_cases',
      trigger: 'summarize_button_clicked',
    });
    setShouldFetchSummary(true);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading Overlay for Summary Generation */}
      {shouldFetchSummary && isLoadingSummary && (
        <OpinionSummaryLoadingOverlay>
          <Box sx={opinionSummaryLoadingCardStyles}>
            <Psychology sx={opinionSummaryLoadingIconStyles} />
            <Stack spacing={2} alignItems="center">
              <Text variant="h3" sx={opinionSummaryLoadingTitleStyles}>
                Please Wait....
              </Text>
              <Text variant="body1" sx={opinionSummaryLoadingTextStyles}>
                Generating Summary of Combined Opinion Report
              </Text>
              <InlineLoading size={40} />
            </Stack>
          </Box>
        </OpinionSummaryLoadingOverlay>
      )}

      <OpinionContentContainer>
        <OpinionContentHeader>
          <OpinionContentTitle variant="body1">
            {cleanOpinionType(opinion.type || 'Unknown')}
            {(opinion as { author_str?: string }).author_str &&
              ` by ${(opinion as { author_str?: string }).author_str}`}
          </OpinionContentTitle>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(opinion as { download_url?: string }).download_url && (
              <StyledActionButton
                component="a"
                href={(opinion as { download_url?: string }).download_url!}
                onClick={handleDownloadClick}
                startIcon={<Download />}
                {...({
                  target: '_blank',
                  rel: 'noopener noreferrer',
                } as Record<string, unknown>)}
              >
                Download PDF
              </StyledActionButton>
            )}
            {shouldShowSummaryButton && (
              <StyledActionButton
                onClick={handleSummarizeClick}
                startIcon={<Psychology />}
                disabled={shouldFetchSummary && isLoadingSummary}
                sx={{
                  opacity: shouldFetchSummary && isLoadingSummary ? 0.6 : 1,
                }}
              >
                {shouldFetchSummary && isLoadingSummary
                  ? 'Summarizing...'
                  : 'Summarize'}
              </StyledActionButton>
            )}
          </Box>
        </OpinionContentHeader>

        {shouldFetchSummary && !isLoadingSummary && (
          <OpinionSummaryContainer sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <OpinionSummaryTitle variant="h4" sx={{ mb: 0 }}>
                Summary
              </OpinionSummaryTitle>
              <AIPill />
            </Box>

            {hasSummaryError || summaryData?.error ? (
              <OpinionSummaryContent
                variant="body1"
                sx={{ color: 'error.main' }}
              >
                Failed to generate summary. Please try again.
              </OpinionSummaryContent>
            ) : (
              <OpinionSummaryContent variant="body1">
                {summaryData?.summary || 'No summary available.'}
              </OpinionSummaryContent>
            )}

            <DisclaimerContainer sx={{ mt: 3 }}>
              <OpinionDisclaimerText>
                <strong>Disclaimer:</strong> Summary includes AI-generated
                content, which may contain errors, inconsistencies, and/or
                outdated information. Such content is provided for informational
                purposes only and should not be relied upon for any specific
                purpose without verification of its accuracy or completeness.
              </OpinionDisclaimerText>
            </DisclaimerContainer>
          </OpinionSummaryContainer>
        )}

        {/* Opinion Text - Content from trusted API source (Court Listener), sanitized before render */}
        {opinion.html_with_citations && (
          <OpinionContentHtml
            dangerouslySetInnerHTML={{
              __html: processHtmlWithCitations(opinion.html_with_citations),
            }}
          />
        )}
      </OpinionContentContainer>
    </Box>
  );
};
